import React, { useState, useRef, useEffect } from 'react'
import { Menu, ChevronUp, ChevronDown, Lock, HelpCircle } from 'lucide-react'
import { useGameplayContext } from '../../contexts/gameplayContext'
import LOCATIONS from '../../constants/locations'
import { Link } from 'react-router-dom'
import { formatCurrency, formatDuration } from '@/utils'
import { STOKE_GAME_SPEED_COSTS } from '@/constants/stoke'
import { GameSpeedTier } from '@/types'
import { Tooltip } from '@nextui-org/react'

interface HeaderProps {
  setIsSidebarOpen: (isOpen: boolean) => void
}

const getLocationAbbreviation = (name: string): string => {
  // Split by spaces or underscores and take first letter of each word
  return name.toUpperCase().slice(0, 3) // Ensure it's max 3 letters
}

export const Header: React.FC<HeaderProps> = ({ setIsSidebarOpen }) => {
  const { gameData, gameSpeed, setGameSpeedWithStoke } = useGameplayContext()
  const location = LOCATIONS[gameData.currentLocation]
  const locationName = (location && location.name) || 'Unknown'
  const isLocationsUnlocked = gameData?.completedQuests?.includes('the_mediterranean_farewell')
  // Check if green wave riding is unlocked
  const isGreenWaveUnlocked = gameData.skills.green_wave_riding?.level > 0

  const [speedMenuOpen, setSpeedMenuOpen] = useState(false)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const speedMenuRef = useRef<HTMLDivElement>(null)
  const speedButtonRef = useRef<HTMLButtonElement>(null)

  // Close the speed menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        speedMenuRef.current &&
        !speedMenuRef.current.contains(event.target as Node) &&
        speedButtonRef.current &&
        !speedButtonRef.current.contains(event.target as Node)
      ) {
        setSpeedMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSpeedChange = (speed: GameSpeedTier) => {
    setGameSpeedWithStoke(speed)
    setSpeedMenuOpen(false)
  }

  const stokeTooltipContent = (
    <div className="max-w-md space-y-1 p-2">
      <p className="pt-2 pb-2 border-b border-gray-300">
        Stoke 🤙 is a special resource unlocked when you can surf green waves, representing your surfing excitement and
        momentum!
      </p>
      <p className="pt-2 pb-2 border-b border-gray-300">
        You earn Stoke while surfing green waves and can spend it to speed up the game, making routine tasks faster.
      </p>
      <p className="pt-2 pb-2 border-b border-gray-300">
        Higher game speeds cost more Stoke per minute - manage it wisely!
      </p>
      <p className="pt-2 pb-2 border-b border-gray-300">
        While idle or away from the game, you'll passively earn Stoke based on your current multipliers.
      </p>
      <p className="pt-2 pb-2">
        💡 Complete side quests to unlock higher game speeds, increase your Stoke multipliers, and expand your Stoke
        capacity!
      </p>
    </div>
  )

  return (
    <header className="relative p-1 flex justify-between items-center shadow-lg z-40 bg-gradient-to-r from-blue-600/70 to-blue-800/70 font-lexend">
      <div className="flex items-center gap-4 font-bold">
        <div className="relative flex items-center min-w-[64px]">
          <Link to="/">
            <img src="/assets/the_goat_surfer_sm.png" alt="GOAT Surfer" className="w-16 h-16" />
          </Link>

          <div className="absolute bottom-0 flex flex-col items-center justify-center text-white lg:relative lg:inset-auto lg:flex-row lg:items-baseline lg:gap-2 lg:ml-2">
            <Link to="/">
              <span className="text-xs lg:text-2xl font-bold leading-tight drop-shadow-md">GOAT&nbsp;Surfer</span>
            </Link>
          </div>
        </div>
        <div className="flex flex-col gap-1 lg:ml-3">
          <div className="text-sm bg-white/20 rounded-full px-3 py-1 backdrop-blur-sm text-white w-24">
            💰${formatCurrency(gameData.currencies?.money?.amount || 0)}
          </div>
          <div className="text-sm bg-white/20 rounded-full px-3 py-1 backdrop-blur-sm text-white w-24">
            ⚡{formatCurrency(gameData.currencies?.energy?.amount || 0)}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1 flex-1 mx-1 lg:flex-initial lg:mx-0">
        <div className="text-sm bg-white/20 rounded-full px-4 py-1 backdrop-blur-sm text-white font-bold whitespace-nowrap">
          {isLocationsUnlocked ? (
            <Link to="/locations" className="hover:underline">
              📍 <span className="hidden md:inline">{locationName}</span>
              <span className="inline md:hidden">{getLocationAbbreviation(locationName)}</span>
              {': '}
              <span className="inline-block max-w-[100px] md:max-w-[200px] truncate align-bottom">
                {gameData.currentLocationSpot}
              </span>
            </Link>
          ) : (
            <span>
              📍 <span className="hidden md:inline">{locationName}</span>
              <span className="inline md:hidden">{getLocationAbbreviation(locationName)}</span>
              {': '}
              <span className="inline-block max-w-[100px] md:max-w-[200px] truncate align-bottom">
                {gameData.currentLocationSpot}
              </span>
            </span>
          )}
        </div>
        <div className="relative">
          <button
            ref={speedButtonRef}
            onClick={() => isGreenWaveUnlocked && setSpeedMenuOpen(!speedMenuOpen)}
            className={`w-full text-sm bg-white/20 rounded-full px-4 py-1 backdrop-blur-sm text-white font-courrier ${
              isGreenWaveUnlocked ? 'hover:bg-white/30 transition-colors' : 'cursor-not-allowed'
            }`}
          >
            <div className="flex items-center justify-between">
              {isGreenWaveUnlocked ? (
                <>
                  <span>
                    ▶️ <span className="text-gray-300 font-bold">{gameSpeed}x</span> 🕒{' '}
                    {formatDuration(gameData.gameMinutes, true)}
                  </span>
                  {speedMenuOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </>
              ) : (
                <>
                  <span>🕒 {formatDuration(gameData.gameMinutes, true)}</span>
                  <span className="flex items-center text-gray-300">
                    <Lock className="h-4 w-4 mr-1" /> Locked
                  </span>
                </>
              )}
            </div>
          </button>

          {speedMenuOpen && isGreenWaveUnlocked && (
            <div
              ref={speedMenuRef}
              style={{
                position: 'fixed',
                width: '200px',
                right: window.innerWidth - (speedButtonRef.current?.getBoundingClientRect().right || 0),
                top: (speedButtonRef.current?.getBoundingClientRect().bottom || 0) + 4,
                zIndex: 9999,
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)'
              }}
              className="bg-blue-900 rounded-md border border-blue-700 overflow-hidden"
            >
              <div className="p-3 border-b border-blue-800 bg-blue-950 relative">
                <div className="flex items-center mb-2">
                  <span className="text-yellow-400 font-semibold">🤙 Stoke:&nbsp;</span>
                  <span className="text-yellow-400 font-semibold">
                    {formatCurrency(gameData.currencies.stoke.amount)} /{' '}
                    {formatCurrency(gameData.currencies.stoke.max || 1)}
                  </span>
                </div>

                {/* Stoke help tooltip button */}
                <Tooltip
                  content={stokeTooltipContent}
                  isOpen={isTooltipOpen}
                  onOpenChange={(open) => setIsTooltipOpen(open)}
                  className="bg-white backdrop-blur-sm text-sm"
                  placement="bottom"
                >
                  <button
                    className="absolute top-3 right-2 text-white hover:text-blue-300 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsTooltipOpen(!isTooltipOpen)
                    }}
                  >
                    <HelpCircle className="w-5 h-5" />
                  </button>
                </Tooltip>

                {/* Stoke progress bar */}
                <div className="w-full h-2 bg-blue-800 rounded-full mb-2 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-yellow-500 to-yellow-300 rounded-full"
                    style={{
                      width: `${Math.min(
                        100,
                        (gameData.currencies.stoke.amount / (gameData.currencies.stoke.max || 1)) * 100
                      )}%`
                    }}
                  />
                </div>

                <div className="text-xs text-blue-300">
                  Stoke helps you breeze through routine tasks and focus on what matters 🏄
                </div>
              </div>

              <div className="max-h-60 overflow-y-auto">
                {gameData.currencies.stoke.unlockedLevels?.map((speed) => {
                  const isActive = gameSpeed === speed
                  const cost = STOKE_GAME_SPEED_COSTS[speed as GameSpeedTier]
                  const affordable = gameData.currencies.stoke.amount >= cost || cost === 0

                  return (
                    <button
                      key={speed}
                      onClick={() => handleSpeedChange(speed as GameSpeedTier)}
                      disabled={!affordable && !isActive}
                      className={`w-full text-left p-2 flex justify-between items-center ${
                        isActive
                          ? 'bg-blue-700 text-white'
                          : affordable
                          ? 'hover:bg-blue-800 text-gray-100'
                          : 'opacity-50 cursor-not-allowed text-gray-300'
                      }`}
                    >
                      <span className="font-semibold">{speed === 1 ? 'Normal' : `${speed}x speed`}</span>
                      <span className="text-xs">
                        {cost === 0 ? (
                          <span className="text-green-400">Free</span>
                        ) : (
                          <span className="text-yellow-400">{cost} 🤙/min</span>
                        )}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <button className="lg:hidden text-white" onClick={() => setIsSidebarOpen(true)}>
        <Menu className="w-8 h-8" />
      </button>
    </header>
  )
}
